/**
 * MinimapOverlay — Toggle button (bottom-left) that expands to show a minimap
 * of the camera path through the shop section.
 *
 * Data: Loads minimap_nav.json from the reconstruction directory in blob storage.
 * Renders: Camera path polyline, structure lines, current position dot.
 * UX: 44px frosted glass toggle button → expands to 160×160px panel, auto-collapse after 10s.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '../../store/appStore'
import { resolveUrl, toBlobUrl } from '../../config'
import type { MinimapNavData } from '../../types/MinimapTypes'

// ─── Constants ────────────────────────────────────────────────────────────────

const BUTTON_SIZE = 44
const PANEL_WIDTH = 160
const PANEL_HEIGHT = 160
const CANVAS_PADDING = 12
const AUTO_COLLAPSE_MS = 10_000
const POSITION_LEFT = 16
const POSITION_BOTTOM = 90 // Above the bottom edge, clear of other controls
const HIT_TOLERANCE_PX = 10 // Tap tolerance for structure line detection
const TAP_PULSE_MS = 600 // Duration of tap feedback animation

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Minimum distance from point (px,py) to segment (ax,ay)→(bx,by) */
function pointToSegmentDist(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - ax, py - ay)
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

/** Stored coordinate-system info so click handler can invert canvas→normalised */
interface BoundsData {
  minX: number; minY: number
  rangeX: number; rangeY: number
  mX: number; mY: number
  drawW: number; drawH: number
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MinimapOverlay() {
  const [isExpanded, setIsExpanded] = useState(false)
  const [navData, setNavData] = useState<MinimapNavData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const lastFetchedPathRef = useRef<string | null>(null)
  const boundsDataRef = useRef<BoundsData | null>(null)
  const isDraggingRef = useRef(false)
  const [tapFeedback, setTapFeedback] = useState<{ x: number; y: number } | null>(null)

  // Store selectors — only primitives or stable references
  const currentSectionFramesPath = useAppStore((s) => s.currentSectionFramesPath)
  const manuallySelectedFrameIndex = useAppStore((s) => s.manuallySelectedFrameIndex)
  const animatingFrameIndex = useAppStore((s) => s.animatingFrameIndex)
  const cameras = useAppStore((s) => s.cameras)
  const tapPoint3D = useAppStore((s) => s.tapPoint3D)
  const tapDiscoveryFrameNumbers = useAppStore((s) => s.tapDiscoveryFrameNumbers)

  // Derive reconstruction dir from frames path
  const reconstructionDir = useMemo(() => {
    if (!currentSectionFramesPath) return null
    // Strip trailing "/frames" to get reconstruction dir
    const idx = currentSectionFramesPath.lastIndexOf('/frames')
    if (idx === -1) return currentSectionFramesPath
    return currentSectionFramesPath.substring(0, idx)
  }, [currentSectionFramesPath])

  // ─── Load minimap_nav.json when section changes ─────────────────────────

  useEffect(() => {
    if (!reconstructionDir) {
      setNavData(null)
      setLoadError(false)
      return
    }

    const minimapUrl = `${reconstructionDir}/minimap_nav.json`

    // Avoid re-fetching the same path
    if (lastFetchedPathRef.current === minimapUrl) return
    lastFetchedPathRef.current = minimapUrl

    let cancelled = false
    setIsLoading(true)
    setLoadError(false)

    const resolvedUrl = resolveUrl(minimapUrl)
    const fetchUrl = toBlobUrl(resolvedUrl)

    console.log(`🗺️ [MinimapOverlay] Loading minimap_nav.json`, {
      currentSectionFramesPath,
      reconstructionDir,
      minimapUrl,
      resolvedUrl,
      fetchUrl: fetchUrl.substring(0, 120) + '...',
    })

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<MinimapNavData>
      })
      .then((data) => {
        if (!cancelled) {
          console.log(`🗺️ [MinimapOverlay] ✅ Loaded minimap data:`, data.frame_numbers?.length, 'frames,', data.structure_lines?.length, 'lines')
          setNavData(data)
          setIsLoading(false)
          // Populate store so DirectHitStrategy can use corridor directions
          useAppStore.getState().setMinimapNavData(data)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn(`🗺️ [MinimapOverlay] ❌ Failed to load minimap_nav.json:`, err.message, '\n  URL:', fetchUrl.substring(0, 150))
          setNavData(null)
          setIsLoading(false)
          setLoadError(true)
          useAppStore.getState().setMinimapNavData(null)
        }
      })

    return () => { cancelled = true }
  }, [reconstructionDir])

  // ─── Auto-collapse timer ────────────────────────────────────────────────

  const resetCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
    }
    collapseTimerRef.current = setTimeout(() => {
      setIsExpanded(false)
    }, AUTO_COLLAPSE_MS)
  }, [])

  useEffect(() => {
    if (isExpanded) {
      resetCollapseTimer()
    }
    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current)
        collapseTimerRef.current = null
      }
    }
  }, [isExpanded, resetCollapseTimer])

  // Reset auto-collapse timer when the current frame changes (user is navigating)
  useEffect(() => {
    if (isExpanded) {
      resetCollapseTimer()
    }
  }, [manuallySelectedFrameIndex, isExpanded, resetCollapseTimer])

  // ─── Canvas rendering ──────────────────────────────────────────────────

  const currentFrameIdx = animatingFrameIndex ?? manuallySelectedFrameIndex ?? 0

  useEffect(() => {
    if (!isExpanded || !navData || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const drawW = PANEL_WIDTH - CANVAS_PADDING * 2
    const drawH = PANEL_HEIGHT - CANVAS_PADDING * 2

    canvas.width = drawW * dpr
    canvas.height = drawH * dpr
    canvas.style.width = `${drawW}px`
    canvas.style.height = `${drawH}px`
    ctx.scale(dpr, dpr)

    // Clear
    ctx.clearRect(0, 0, drawW, drawH)

    const path = navData.camera_path_2d
    const frameNums = navData.frame_numbers

    if (path.length === 0) return

    // Camera path is already normalized to [0,1]. Compute bounding box for tighter fit.
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    for (const [px, py] of path) {
      if (px < minX) minX = px
      if (px > maxX) maxX = px
      if (py < minY) minY = py
      if (py > maxY) maxY = py
    }
    // Include structure lines in bounds
    for (const line of navData.structure_lines) {
      if (line.x1 < minX) minX = line.x1
      if (line.x2 < minX) minX = line.x2
      if (line.x1 > maxX) maxX = line.x1
      if (line.x2 > maxX) maxX = line.x2
      if (line.y1 < minY) minY = line.y1
      if (line.y2 < minY) minY = line.y2
      if (line.y1 > maxY) maxY = line.y1
      if (line.y2 > maxY) maxY = line.y2
    }
    const rangeX = maxX - minX || 1
    const rangeY = maxY - minY || 1
    const margin = 0.08 // 8% margin
    const mX = rangeX * margin
    const mY = rangeY * margin

    // Persist bounds so click handler can invert canvas→normalised
    boundsDataRef.current = { minX, minY, rangeX, rangeY, mX, mY, drawW, drawH }

    // Map normalized [0,1] coords to canvas pixel coords
    const toCanvasX = (nx: number) => ((nx - minX + mX) / (rangeX + 2 * mX)) * drawW
    const toCanvasY = (ny: number) => ((ny - minY + mY) / (rangeY + 2 * mY)) * drawH

    // 0. Density grid background (v3+ only)
    if (navData.density_grid && navData.density_grid.length > 0) {
      const grid = navData.density_grid
      const gridH = grid.length
      const gridW = grid[0].length
      // Density grid covers [0,1]×[0,1] normalized space (same as camera_path_2d)
      // Map each cell to canvas coordinates
      for (let row = 0; row < gridH; row++) {
        for (let col = 0; col < gridW; col++) {
          const val = grid[row][col]
          if (val < 10) continue  // Skip near-empty cells for performance
          const alpha = Math.min(0.35, (val / 255) * 0.4)
          // Cell bounds in normalized [0,1] space
          const nx0 = col / gridW
          const ny0 = row / gridH
          const nx1 = (col + 1) / gridW
          const ny1 = (row + 1) / gridH
          const cx0 = toCanvasX(nx0)
          const cy0 = toCanvasY(ny0)
          const cx1 = toCanvasX(nx1)
          const cy1 = toCanvasY(ny1)
          // Warm amber color ramp
          const r = 245
          const g = Math.round(158 + (1 - val / 255) * 60) // amber-to-yellow
          const b = 50
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`
          ctx.fillRect(cx0, cy0, cx1 - cx0, cy1 - cy0)
        }
      }
    }

    // 1. Structure lines (faint)
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'
    ctx.lineWidth = 1
    for (const line of navData.structure_lines) {
      ctx.beginPath()
      ctx.moveTo(toCanvasX(line.x1), toCanvasY(line.y1))
      ctx.lineTo(toCanvasX(line.x2), toCanvasY(line.y2))
      ctx.stroke()
    }

    // 2. Camera path (polyline)
    ctx.strokeStyle = 'rgba(255,255,255,0.5)'
    ctx.lineWidth = 1.5
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.beginPath()
    for (let i = 0; i < path.length; i++) {
      const x = toCanvasX(path[i][0])
      const y = toCanvasY(path[i][1])
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.stroke()

    // 2b. Discovery camera highlights (Step 12)
    // Orange dots for cameras that can see the tapped 3D point
    if (tapDiscoveryFrameNumbers && tapDiscoveryFrameNumbers.size > 0) {
      for (let i = 0; i < path.length; i++) {
        if (tapDiscoveryFrameNumbers.has(frameNums[i])) {
          const dx = toCanvasX(path[i][0])
          const dy = toCanvasY(path[i][1])
          // Orange glow
          ctx.beginPath()
          ctx.arc(dx, dy, 5, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(251, 146, 60, 0.35)'
          ctx.fill()
          // Orange dot
          ctx.beginPath()
          ctx.arc(dx, dy, 3, 0, Math.PI * 2)
          ctx.fillStyle = '#FB923C'
          ctx.fill()
        }
      }
    }

    // 3. Current position dot
    const frameIdxInPath = frameNums.indexOf(currentFrameIdx)
    if (frameIdxInPath !== -1 && frameIdxInPath < path.length) {
      const cx = toCanvasX(path[frameIdxInPath][0])
      const cy = toCanvasY(path[frameIdxInPath][1])

      // Glow
      ctx.beginPath()
      ctx.arc(cx, cy, 6, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(59, 130, 246, 0.3)'
      ctx.fill()

      // Inner dot
      ctx.beginPath()
      ctx.arc(cx, cy, 3.5, 0, Math.PI * 2)
      ctx.fillStyle = '#3B82F6'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    // 4. Path start indicator (small triangle)
    if (path.length > 1) {
      const sx = toCanvasX(path[0][0])
      const sy = toCanvasY(path[0][1])
      ctx.fillStyle = 'rgba(34, 197, 94, 0.7)'
      ctx.beginPath()
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2)
      ctx.fill()
    }

    // 5. Tapped 3D point projection (Step 11)
    // Project the 3D world point onto the minimap floor plane
    if (tapPoint3D && navData.floor_projection) {
      const { axis1, axis2, centroid } = navData.floor_projection
      // PCA projection: dot(point - centroid, axis)
      const dx = tapPoint3D[0] - centroid[0]
      const dy = tapPoint3D[1] - centroid[1]
      const dz = tapPoint3D[2] - centroid[2]
      const u = dx * axis1[0] + dy * axis1[1] + dz * axis1[2]
      const v = dx * axis2[0] + dy * axis2[1] + dz * axis2[2]

      // Apply rotation (same as Python's align_rotation)
      const rotDeg = navData.rotation_angle ?? 0
      const rotRad = (rotDeg * Math.PI) / 180
      const cosA = Math.cos(rotRad)
      const sinA = Math.sin(rotRad)
      const u2 = u * cosA - v * sinA
      const v2 = u * sinA + v * cosA

      // Normalize to [0,1] using bounds (same as Python path normalization)
      const [bMinX, bMaxX, bMinY, bMaxY] = navData.bounds
      const bRangeX = bMaxX - bMinX || 1
      const bRangeY = bMaxY - bMinY || 1
      const nx = (u2 - bMinX) / bRangeX
      const ny = 1.0 - (v2 - bMinY) / bRangeY // Y flipped (same as Python)

      // Map to canvas
      const px = toCanvasX(nx)
      const py = toCanvasY(ny)

      // Red glow
      ctx.beginPath()
      ctx.arc(px, py, 7, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(239, 68, 68, 0.3)'
      ctx.fill()

      // Red dot
      ctx.beginPath()
      ctx.arc(px, py, 4, 0, Math.PI * 2)
      ctx.fillStyle = '#EF4444'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 1
      ctx.stroke()
    }
  }, [isExpanded, navData, currentFrameIdx, tapPoint3D, tapDiscoveryFrameNumbers])

  // ─── Core navigation: canvas coords → best camera ─────────────────────
  // Shared by tap and drag. Returns the frame navigated to, or null.
  const navigateToCanvasPoint = useCallback(
    (clickX: number, clickY: number): number | null => {
      if (!navData || !boundsDataRef.current) return null

      const { minX, minY, rangeX, rangeY, mX, mY, drawW, drawH } = boundsDataRef.current

      const toNormX = (cx: number) => cx / drawW * (rangeX + 2 * mX) + minX - mX
      const toNormY = (cy: number) => cy / drawH * (rangeY + 2 * mY) + minY - mY
      const toCanvasX = (nx: number) => ((nx - minX + mX) / (rangeX + 2 * mX)) * drawW
      const toCanvasY = (ny: number) => ((ny - minY + mY) / (rangeY + 2 * mY)) * drawH

      const path = navData.camera_path_2d
      const frameNums = navData.frame_numbers
      const corridorDirs = navData.corridor_directions

      // Priority: discovery cameras
      if (tapDiscoveryFrameNumbers && tapDiscoveryFrameNumbers.size > 0) {
        let nearestDiscoveryDist = Infinity
        let nearestDiscoveryFrame: number | null = null
        for (let i = 0; i < path.length; i++) {
          if (!tapDiscoveryFrameNumbers.has(frameNums[i])) continue
          const cx = toCanvasX(path[i][0])
          const cy = toCanvasY(path[i][1])
          const d = Math.hypot(clickX - cx, clickY - cy)
          if (d < nearestDiscoveryDist) {
            nearestDiscoveryDist = d
            nearestDiscoveryFrame = frameNums[i]
          }
        }
        if (nearestDiscoveryFrame !== null && nearestDiscoveryDist <= HIT_TOLERANCE_PX * 1.5) {
          useAppStore.getState().setManuallySelectedFrame(nearestDiscoveryFrame)
          useAppStore.getState().centerViewportOnFrame(nearestDiscoveryFrame)
          return nearestDiscoveryFrame
        }
      }

      // Structure line check
      let nearStructureLine = false
      for (const line of navData.structure_lines) {
        const dist = pointToSegmentDist(
          clickX, clickY,
          toCanvasX(line.x1), toCanvasY(line.y1),
          toCanvasX(line.x2), toCanvasY(line.y2),
        )
        if (dist <= HIT_TOLERANCE_PX) {
          nearStructureLine = true
          break
        }
      }

      let bestCameraIdx: number | null = null

      if (nearStructureLine) {
        const tapNx = toNormX(clickX)
        const tapNy = toNormY(clickY)
        let bestScore = -Infinity
        for (let i = 0; i < path.length; i++) {
          const [px, py] = path[i]
          const dx = tapNx - px
          const dy = tapNy - py
          const dist = Math.hypot(dx, dy)
          if (dist < 1e-9) continue
          const corridorRad = (corridorDirs[i] ?? 0) * Math.PI / 180
          const angleToTap = Math.atan2(dy, dx)
          let angleDiff = angleToTap - corridorRad
          angleDiff = Math.atan2(Math.sin(angleDiff), Math.cos(angleDiff))
          if (Math.abs(angleDiff) > Math.PI / 2) continue
          const facingScore = Math.cos(angleDiff)
          const distScore = 1 / (1 + dist * 10)
          const score = facingScore * 0.7 + distScore * 0.3
          if (score > bestScore) {
            bestScore = score
            bestCameraIdx = frameNums[i]
          }
        }
        if (bestCameraIdx === null) nearStructureLine = false
      }

      if (!nearStructureLine) {
        let minDist = Infinity
        let nearestPathIdx = 0
        for (let i = 0; i < path.length; i++) {
          const cx = toCanvasX(path[i][0])
          const cy = toCanvasY(path[i][1])
          const d = Math.hypot(clickX - cx, clickY - cy)
          if (d < minDist) {
            minDist = d
            nearestPathIdx = i
          }
        }
        bestCameraIdx = frameNums[nearestPathIdx]
      }

      if (bestCameraIdx !== null) {
        useAppStore.getState().setManuallySelectedFrame(bestCameraIdx)
        useAppStore.getState().centerViewportOnFrame(bestCameraIdx)
      }

      return bestCameraIdx
    },
    [navData, tapDiscoveryFrameNumbers],
  )

  // ─── Pointer (mouse + touch) navigation handlers ───────────────────────

  const handlePointerDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      isDraggingRef.current = true
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const best = navigateToCanvasPoint(x, y)
      if (best !== null) {
        setTapFeedback({ x: x + CANVAS_PADDING, y: y + CANVAS_PADDING })
        setTimeout(() => setTapFeedback(null), TAP_PULSE_MS)
      }
      resetCollapseTimer()
    },
    [navigateToCanvasPoint, resetCollapseTimer],
  )

  const handlePointerMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDraggingRef.current) return
      const rect = e.currentTarget.getBoundingClientRect()
      navigateToCanvasPoint(e.clientX - rect.left, e.clientY - rect.top)
    },
    [navigateToCanvasPoint],
  )

  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // Attach a non-passive touchmove listener directly on the canvas so we can
  // call preventDefault() and prevent the underlying viewer from scrolling
  // while the user scrubs the minimap. React registers touch listeners as
  // passive by default, so e.preventDefault() inside onTouchMove is a no-op.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const onNativeTouchMove = (e: TouchEvent) => {
      if (isDraggingRef.current) e.preventDefault()
    }
    canvas.addEventListener('touchmove', onNativeTouchMove, { passive: false })
    return () => canvas.removeEventListener('touchmove', onNativeTouchMove)
  }, [isExpanded]) // re-attach when canvas mounts (isExpanded gates canvas render)

  // ─── Toggle handler ────────────────────────────────────────────────────

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // ─── Don't render if no data available ──────────────────────────────────

  if (!reconstructionDir || loadError) return null
  if (!navData && !isLoading) return null

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        left: POSITION_LEFT,
        bottom: POSITION_BOTTOM,
        zIndex: 1000002,
        pointerEvents: 'auto',
      }}
    >
      {/* Toggle button */}
      <button
        onClick={handleToggle}
        style={{
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          borderRadius: '50%',
          border: 'none',
          background: isExpanded
            ? 'rgba(59, 130, 246, 0.25)'
            : 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          color: '#fff',
          fontSize: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transition: 'background 0.2s, transform 0.2s',
          pointerEvents: 'auto',
        }}
        aria-label={isExpanded ? 'Hide minimap' : 'Show minimap'}
      >
        {/* Map icon (simple SVG) */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
          <line x1="8" y1="2" x2="8" y2="18" />
          <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
      </button>

      {/* Expanded minimap panel */}
      {isExpanded && (
        <div
          style={{
            position: 'absolute',
            bottom: BUTTON_SIZE + 8,
            left: 0,
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            borderRadius: 12,
            background: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            overflow: 'hidden',
            animation: 'minimapFadeIn 0.2s ease-out',
          }}
        >
          {isLoading || !navData ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.4)',
                fontSize: 12,
              }}
            >
              Loading...
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={handlePointerUp}
              onTouchStart={(e) => {
                e.stopPropagation()
                isDraggingRef.current = true
                const touch = e.touches[0]
                const rect = e.currentTarget.getBoundingClientRect()
                const x = touch.clientX - rect.left
                const y = touch.clientY - rect.top
                const best = navigateToCanvasPoint(x, y)
                if (best !== null) {
                  setTapFeedback({ x: x + CANVAS_PADDING, y: y + CANVAS_PADDING })
                  setTimeout(() => setTapFeedback(null), TAP_PULSE_MS)
                }
                resetCollapseTimer()
              }}
              onTouchMove={(e) => {
                e.stopPropagation()
                if (!isDraggingRef.current) return
                const touch = e.touches[0]
                const rect = e.currentTarget.getBoundingClientRect()
                navigateToCanvasPoint(touch.clientX - rect.left, touch.clientY - rect.top)
              }}
              onTouchEnd={(e) => {
                e.stopPropagation()
                isDraggingRef.current = false
              }}
              style={{
                display: 'block',
                margin: `${CANVAS_PADDING}px`,
                cursor: 'pointer',
                touchAction: 'none',
              }}
            />
          )}

          {/* Tap feedback pulse */}
          {tapFeedback && (
            <div
              style={{
                position: 'absolute',
                left: tapFeedback.x,
                top: tapFeedback.y,
                width: 0,
                height: 0,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, -50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '2px solid rgba(59,130,246,0.8)',
                  animation: `minimapTapPulse ${TAP_PULSE_MS}ms ease-out forwards`,
                }}
              />
            </div>
          )}

          {/* Frame counter badge */}
          {navData && (
            <div
              style={{
                position: 'absolute',
                bottom: 4,
                right: 6,
                fontSize: 9,
                color: 'rgba(255,255,255,0.35)',
                fontFamily: 'monospace',
              }}
            >
              {currentFrameIdx + 1}/{cameras.length}
            </div>
          )}
        </div>
      )}

      {/* CSS keyframes for animations */}
      <style>{`
        @keyframes minimapFadeIn {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes minimapTapPulse {
          0%   { transform: translate(-50%,-50%) scale(0.3); opacity: 1; }
          100% { transform: translate(-50%,-50%) scale(1.8); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
